// What's New entries for the Home tab, newest first.
//
// `id` is a stable slug, not the app's package.json version: not every
// release ships a user-facing feature, and not every user-facing feature
// ships in its own release, so the two numbers would drift immediately.
// useWhatsNewState compares this id against the last one a viewer acted on,
// so a new entry always starts expanded even if the last one was left
// collapsed.
//
// Each item is one short, scannable line, not a paragraph: the card has to
// read in a glance on Home, not double as release notes. `linkTo` is a
// route relative to `/:leagueId/` (WhatsNewCard fills in the id), and is
// only rendered when a viewer belongs to exactly one league. Omit it for an
// item with nowhere specific to send a manager. `subItems` is an optional
// list of plain strings shown as indented bullets under their item, for detail
// that belongs to one headline rather than standing on its own. `isNew` marks a feature that
// didn't exist before this release, as opposed to a change to one that did
// (an "improved" section, a definition), and gets a [NEW] tag on the bullet.
//
// Every entry opens with the ? tour item. The tour button at the top left of
// each page is the best way in to the app, and managers forget it is there,
// so each new entry points at it again before its own news.
export const CHANGELOG = [
  {
    id: "2026-10-league-cards",
    date: "October 2026",
    title: "Pick Three and a better league card",
    items: [
      { text: "Tap any ? for a quick guide to that page or section." },
      {
        text: "Pick Three: a free weekly game on Home.",
        isNew: true,
        subItems: [
          "Pick the three highest scoring teams. Your captain counts double.",
          "Friends can play at lineupcfb.com/pick-three.",
        ],
      },
      {
        text: "Rebuilt league cards on Home.",
        isNew: true,
        subItems: [
          "Breaking news, the table and your lineup at a glance.",
          "Swipe between leagues.",
        ],
      },
    ],
  },
  {
    id: "2026-10-pick-three",
    date: "October 2026",
    title: "Pick Three",
    items: [
      { text: "Not sure how something works? Tap the ? at the top left of any page and it explains the features on that page." },
      {
        text: "Introducing Pick Three, a free weekly game on Home. Pick the three teams you think score the most Lineup points, and make one your captain.",
        isNew: true,
        subItems: [
          "Your captain's points count double, wins and losses alike.",
          "Each team locks 15 minutes before its own kickoff, so you can enter any time before the last game of the week.",
          "When the week ends, see how you placed against everyone who played.",
          "Friends without a league can play too, at lineupcfb.com/pick-three.",
        ],
      },
    ],
  },
  {
    id: "2026-10-daily-games",
    date: "October 2026",
    title: "Daily Games, sharing and help",
    items: [
      {
        text: "Introducing Daily Games, on Home: Higher or Lower and Home Field. They have nothing to do with your league's scoring.",
        isNew: true,
        subItems: [
          "Ten seconds a question, one go a day, and the same questions for everyone.",
          "Share your score when you finish.",
        ],
      },
      { text: "Share a lineup as text from the Lineup tab, or anyone's from the Scoreboard.", linkTo: "scoreboard", linkLabel: "View", isNew: true },
      { text: "Share the standings, or any Hall of Fame record, from the League tab.", linkTo: "league", linkLabel: "View", isNew: true },
      { text: "Tap a freeze in recent activity to see the score at the freeze and what it was worth.", linkTo: "league", linkLabel: "View", isNew: true },
      { text: "Tap the ? beside a section for a short walkthrough of it. Turn the full tutorials back on from Show Tutorial in the profile menu.", isNew: true },
    ],
  },
  {
    id: "2026-09-ranked-claims",
    date: "September 2026",
    title: "Ranked claims, Trending teams and reactions",
    items: [
      {
        text: "Rank your waiver claims. Use the arrows to set the order they're tried in.",
        linkTo: "lineup",
        linkLabel: "View",
        isNew: true,
        subItems: [
          "Bid up to your full budget on more than one team. You only pay for claims that win.",
          "Up to 5 pending claims at a time.",
        ],
      },
      { text: "Trending teams on the Teams tab shows the free agents worth a look this week.", linkTo: "teams", linkLabel: "View", isNew: true },
      { text: "Tap a team's score to see how it was made.", linkTo: "lineup", linkLabel: "View", isNew: true },
      { text: "Team sheets now have Overview, Stats and Schedule tabs.", isNew: true },
      { text: "Added a dark mode and light mode toggle.", isNew: true },
    ],
  },
  {
    // Renamed from 2026-09-top-teams when the waiver items were added, so the
    // card opens expanded again for anyone who had collapsed it. The Top 25
    // release never reached the public, so its items stay alongside.
    id: "2026-09-waivers-top-teams",
    date: "September 2026",
    title: "Waivers, Top 25, Weekly Awards and your Watchlist",
    items: [
      {
        text: "Waivers open for the first time on Sunday, September 20 at 10am ET.",
        linkTo: "teams",
        linkLabel: "View",
        isNew: true,
        subItems: [
          "Every week, bid on unowned teams from 10am Sunday to 4am Tuesday ET. Bids are sealed, the highest wins, and your budget resets each week.",
          "Budgets run in reverse standings order: last place gets $100 and each place higher gets $5 less, so the manager at the bottom has the best shot at the one team they want.",
          "Bids resolve at 6am Tuesday. Free agency then opens, so you can add any unowned team instantly for the rest of the week. Each team closes when its game locks, 15 minutes before kickoff.",
        ],
      },
      { text: "NCAA Top 25, further down this page, ranks the highest-scoring college teams for the latest week or the season so far, with how widely each one is owned.", isNew: true },
      { text: "Weekly Awards, at the bottom of this page, shows the best and worst lineup across every league from last week, with all five starters.", isNew: true },
      { text: "Star a team from its team sheet to add it to your Watchlist, then filter the Teams tab to just those teams.", linkTo: "teams", linkLabel: "View", isNew: true },
      { text: "Tap the arrow on a standings row to see that manager's starters for the week without leaving the table.", linkTo: "league", linkLabel: "View", isNew: true },
    ],
  },
  {
    id: "2026-09-hall-of-fame",
    date: "September 2026",
    title: "Hall of Fame and Hall of Shame",
    items: [
      { text: "Hall of Fame and Hall of Shame now sit right under your league's standings table.", linkTo: "league", linkLabel: "View", isNew: true },
      { text: "Scroll right on standings for new stats: decision accuracy, captain accuracy, and free agency moves.", linkTo: "league", linkLabel: "View", isNew: true },
      { text: "Standings rows now show a dot per starter: blue for final, green for live, grey for not kicked off.", linkTo: "league", linkLabel: "View", isNew: true },
      { text: "Introducing the Perfect Week stat: no benched team outscored a starter, and the captain was the best of the lineup.", isNew: true },
      { text: "Scoreboard tab has an improved Weekly Highlights section: best and worst week, captain calls, bench, and perfect weeks.", linkTo: "scoreboard", linkLabel: "View" },
      { text: "Feedback and bug reports you send from the app now reach us directly. Send one from your profile menu, tap your avatar, top right.", isNew: true },
    ],
  },
];
